"use client";

import React, { useState, useRef, useEffect } from "react";
import { Input } from "../../components/ui/input-field";
import { ChevronDown } from "lucide-react";

const BUSINESS_TYPES = [
  "Sole Trader",
  "Partnership",
  "Private Company (Pty Ltd)",
  "Public Company",
  "Trust",
  "NGO / Non-Profit",
  "Other",
];

const INDUSTRY_SECTORS = [
  "Financial Services",
  "Technology",
  "Retail",
  "E-commerce",
  "Consulting",
  "Marketing",
  "Other",
];

function RadioSelect({
  label,
  placeholder,
  options,
  value,
  onChange,
  state,
  helperText,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const stateStyle = state === "error" ? "border-red-500" : "border-[#CECBD7]";

  return (
    <div className="w-full space-y-3" ref={ref}>
      {label && (
        <p className="text-[14px] text-[#6D6584] leading-[20px]">{label}</p>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={`flex items-center justify-between w-full px-4 h-[56px] text-left bg-white border rounded-[16px] ${stateStyle}`}
        >
          <span
            className={`text-[16px] leading-[24px] ${value ? "text-[#423E51]" : "text-[#9D96AF]"}`}
          >
            {value || placeholder}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-[#6D6584] transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-[16px] border border-[#ebe9ef] shadow-lg z-20 overflow-hidden">
            <div className="max-h-[340px] overflow-y-auto">
              {options.map((option) => {
                const isSelected = value === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      onChange(option);
                      setOpen(false);
                    }}
                    className="flex items-center gap-4 w-full px-6 py-4 hover:bg-gray-50 transition text-left"
                  >
                    {/* Radio circle */}
                    <div
                      className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        isSelected ? "border-[#EB3366]" : "border-[#CECBD7]"
                      }`}
                    >
                      {isSelected && (
                        <div className="w-3.5 h-3.5 rounded-full bg-[#EB3366]" />
                      )}
                    </div>
                    <span
                      className={`text-[16px] font-semibold leading-[24px] ${
                        isSelected ? "text-[#423E51]" : "text-[#6D6584]"
                      }`}
                    >
                      {option}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {helperText && state === "error" && (
        <p className="text-xs text-red-500">{helperText}</p>
      )}
    </div>
  );
}

export default function BusinessDetailsStep({
  formData,
  updateFormData,
  onNext,
  onBack,
}) {
  const [errors, setErrors] = useState({});
  const businessName = formData.businessName?.trim() || "";
  const businessAddress = formData.businessAddress?.trim() || "";
  const registrationNumber = formData.registrationNumber?.trim() || "";
  const websiteUrl = formData.websiteUrl?.trim() || "";
  const isSoleTrader = formData.businessType === "Sole Trader";

  const validate = () => {
    const newErrors = {};

    if (!businessName) {
      newErrors.businessName = "Business name is required";
    }

    if (!formData.businessType) {
      newErrors.businessType = "Please select a business type";
    }

    if (!formData.industrySector) {
      newErrors.industrySector = "Please select an industry / sector";
    }

    if (!businessAddress) {
      newErrors.businessAddress = "Business address is required";
    }

    if (!isSoleTrader && !registrationNumber) {
      newErrors.registrationNumber = "Registration number is required";
    }

    // Registration number format validation (alphanumeric, 5-20 chars)
    if (registrationNumber) {
      if (!/^[a-zA-Z0-9/-]{5,20}$/.test(registrationNumber)) {
        newErrors.registrationNumber =
          "Registration number must be 5-20 alphanumeric characters";
      }
    }

    // URL validation for website if provided
    if (websiteUrl) {
      try {
        const urlToValidate = websiteUrl.includes("://")
          ? websiteUrl
          : `https://${websiteUrl}`;
        new URL(urlToValidate);
      } catch {
        newErrors.websiteUrl = "Please enter a valid URL";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  const isFormValid =
    businessName &&
    formData.businessType &&
    formData.industrySector &&
    businessAddress &&
    (isSoleTrader || registrationNumber);

  return (
    <div className="flex flex-col gap-10 pt-10 pb-16">
      {/* Heading */}
      <div className="flex flex-col gap-6 items-start w-full">
        <h2 className="text-[32px] font-semibold leading-[40px] text-[#33125f] text-center w-full">
          Business details
        </h2>
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-6 w-full">
        {/* Business Name */}
        <Input
          text="Business name"
          placeholder="Enter registered business name"
          value={formData.businessName}
          onChange={(e) => {
            updateFormData({ businessName: e.target.value });
            setErrors((prev) => ({ ...prev, businessName: undefined }));
          }}
          state={errors.businessName ? "error" : "default"}
          helperText={errors.businessName || false}
        />

        {/* Trading Name */}
        <Input
          text="Trading Name  (optional)"
          placeholder="Enter trading name"
          value={formData.tradingName}
          onChange={(e) => updateFormData({ tradingName: e.target.value })}
          state="default"
        />

        {/* Business Type */}
        <RadioSelect
          label="Business Type"
          placeholder="Select Business Type"
          options={BUSINESS_TYPES}
          value={formData.businessType}
          onChange={(val) => {
            updateFormData({ businessType: val });
            setErrors((prev) => ({
              ...prev,
              businessType: undefined,
              registrationNumber: undefined,
            }));
          }}
          state={errors.businessType ? "error" : "default"}
          helperText={errors.businessType}
        />

        {/* Registration Number */}
        <Input
          text={
            isSoleTrader
              ? "Registration Number (Optional for sole traders)"
              : "Registration Number"
          }
          placeholder="Enter company registration number"
          value={formData.registrationNumber}
          onChange={(e) => {
            updateFormData({ registrationNumber: e.target.value });
            setErrors((prev) => ({ ...prev, registrationNumber: undefined }));
          }}
          state={errors.registrationNumber ? "error" : "default"}
          helperText={errors.registrationNumber || false}
        />

        {/* Tax Number */}
        <Input
          text="Tax Number (optional)"
          placeholder="Enter tax number"
          value={formData.taxNumber}
          onChange={(e) => updateFormData({ taxNumber: e.target.value })}
          state="default"
        />

        {/* Industry / Sector */}
        <RadioSelect
          label="Industry / Sector"
          placeholder="Select Industry / Sector"
          options={INDUSTRY_SECTORS}
          value={formData.industrySector}
          onChange={(val) => {
            updateFormData({ industrySector: val });
            setErrors((prev) => ({ ...prev, industrySector: undefined }));
          }}
          state={errors.industrySector ? "error" : "default"}
          helperText={errors.industrySector}
        />

        {/* Website URL */}
        <Input
          text="Website URL (optional)"
          placeholder="Enter website URL"
          value={formData.websiteUrl}
          onChange={(e) => {
            updateFormData({ websiteUrl: e.target.value });
            setErrors((prev) => ({ ...prev, websiteUrl: undefined }));
          }}
          state={errors.websiteUrl ? "error" : "default"}
          helperText={errors.websiteUrl || false}
        />

        {/* Business Address */}
        <Input
          text="Business Address"
          placeholder="Enter business address"
          value={formData.businessAddress}
          onChange={(e) => {
            updateFormData({ businessAddress: e.target.value });
            setErrors((prev) => ({ ...prev, businessAddress: undefined }));
          }}
          state={errors.businessAddress ? "error" : "default"}
          helperText={errors.businessAddress || false}
        />
      </div>

      {/* Buttons */}
      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="h-[48px] w-[150px] border border-[#ebe9ef] rounded-full text-[14px] font-semibold text-[#6d6584] hover:bg-gray-50 transition"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!isFormValid}
          className={`h-[48px] w-[150px] rounded-full text-[14px] font-semibold transition ${
            isFormValid
              ? "text-white cursor-pointer hover:opacity-90"
              : "bg-[#f2f1f4] text-[#9d96af] cursor-not-allowed"
          }`}
          style={
            isFormValid
              ? {
                  backgroundImage:
                    "linear-gradient(65deg, #57249C 10.66%, #EB3366 113.47%)",
                }
              : undefined
          }
        >
          Next
        </button>
      </div>
    </div>
  );
}
